import { DateFormat } from "common";
import { Dayjs } from "dayjs";
import { sumBy } from "lodash";
import { Seller, XolaBotAssistType } from "types";
import { api } from "../api/api";
import { config } from "../config";
import { XolaBotEarningsReportFilter } from "../stores/reports/xolabotEarningsReportFilterStore";

export interface XolaBotEarningsReportParams {
    seller: Seller;
    from: Dayjs;
    to: Dayjs;
    filters: XolaBotEarningsReportFilter;
}

export const getXolaBotElasticsearchParams = async (params: XolaBotEarningsReportParams) => {
    const { seller } = params;
    const index = config.elasticsearch.indexes.purchase;
    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));

    return getReport(data);
};

const getReport = (data: any) => {
    const buckets = data.aggregations.tags.id?.buckets;
    const assistTypes = Object.values(XolaBotAssistType).map((type) => {
        const assistTypeTotal = getAssistTypeTotal(type, buckets);
        return {
            type: type,
            total: assistTypeTotal,
        };
    });
    const total = sumBy(assistTypes, "total");

    return { total, assistTypes };
};

const getAssistTypeTotal = (type: XolaBotAssistType, buckets: any) => {
    let total = 0;
    if (buckets && type in buckets) {
        if (type === XolaBotAssistType.UPSELL_ADDONS) {
            total = buckets[type].purchase.addOns.addons?.amount
                ? buckets[type].purchase.addOns.addons.amount.value
                : 0;
        } else {
            total = buckets[type].purchase.amount.value;
        }
    }

    return total;
};

const getPayload = ({ seller, from, to }: XolaBotEarningsReportParams) => {
    const interventionBuckets: any = {};
    for (const type of Object.values(XolaBotAssistType)) {
        interventionBuckets[type] = {
            match_phrase: {
                "tags.id": type,
            },
        };
    }

    const addOnsAggregation = {
        nested: {
            path: "items.lineItems",
        },
        aggs: {
            addons: {
                filter: {
                    term: {
                        "items.lineItems.type": "merchandise",
                    },
                },
                aggs: {
                    amount: {
                        sum: {
                            field: "items.lineItems.amount",
                        },
                    },
                },
            },
        },
    };

    const netAggregation = {
        tags: {
            nested: {
                path: "tags",
            },
            aggs: {
                id: {
                    filters: {
                        filters: interventionBuckets,
                    },
                    aggs: {
                        purchase: {
                            reverse_nested: {},
                            aggs: {
                                amount: {
                                    sum: {
                                        field: "amount",
                                    },
                                },
                                addOns: addOnsAggregation,
                            },
                        },
                    },
                },
            },
        },
    };

    const start = from.format(DateFormat.DATE_ISO);
    const end = to.format(DateFormat.DATE_ISO);
    const dateRange = { gte: start, lte: end, time_zone: seller.timezoneName };
    const rangeFilter: any = { range: { createdAt: dateRange } };
    const must: any = [rangeFilter, { term: { "seller.id": seller.id } }];

    return {
        size: 0, // Don't show hits
        query: {
            bool: { must },
        },
        aggs: netAggregation,
    };
};
